import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, IconButton } from '../components/Button';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { allModes } from '../engine/registry';
import { HINT_NUDGE_MS, pickNudge } from '../engine/hints';
import { usePhysicalKeyboard } from '../hooks/useKeyboard';
import { useElapsed } from '../hooks/useTimer';
import { useToday } from '../hooks/useToday';
import { gardenFor, nextUnplayed } from '../state/garden';
import { useGame } from '../state/GameContext';
import { useNav } from '../state/NavContext';
import { useSettings } from '../state/SettingsContext';
import { useSiteConfig } from '../state/SiteConfigContext';
import { useStats } from '../state/StatsContext';
import { loadSlice, saveSlice, type SliceConfig } from '../state/persistence';
import { accentVars } from '../utils/accent';
import { commas, formatDuration } from '../utils/format';
import { HowToPlayModal } from './HowToPlayModal';
import { ResultModal, viewFromSession } from './ResultModal';

const seenSlice: SliceConfig<string[]> = { name: 'howto-seen', version: 1, defaults: () => [] };

function ElapsedTime({ startedAt, running }: { startedAt: number; running: boolean }) {
  const ms = useElapsed(startedAt, running);
  return <span className="cv-mono">{formatDuration(ms)}</span>;
}

export function PlayScreen() {
  const game = useGame();
  const { navigate } = useNav();
  const { settings, effectiveReducedMotion } = useSettings();
  const { isModeEnabled } = useSiteConfig();
  const { stats } = useStats();
  const toast = useToast();
  const today = useToday();
  const active = game.active;

  const [howtoOpen, setHowtoOpen] = useState(false);
  const [autoHowto, setAutoHowto] = useState(false);
  const [confirmGiveUp, setConfirmGiveUp] = useState(false);
  const [resultOpen, setResultOpen] = useState(true);
  const nudgedRef = useRef(false);

  // Surface how-to the first time a mode is played.
  useEffect(() => {
    if (!active || active.status !== 'playing') return;
    const seen = loadSlice(seenSlice);
    if (!seen.includes(active.mode.id)) {
      setHowtoOpen(true);
      setAutoHowto(true);
      saveSlice(seenSlice, [...seen, active.mode.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.mode.id]);

  // Re-open the result card whenever a new puzzle finishes.
  const finishedKey = active?.result ? `${active.key}:${active.finishedAt}` : null;
  useEffect(() => {
    if (finishedKey) setResultOpen(true);
  }, [finishedKey]);

  const anyModal = howtoOpen || confirmGiveUp || (!!active?.result && resultOpen);
  usePhysicalKeyboard(game.handleKey, !!active && active.status === 'playing' && !anyModal);

  useEffect(() => {
    nudgedRef.current = false;
  }, [active?.seed.seed]);

  useEffect(() => {
    if (!active || active.status !== 'playing' || !settings.hintNudges || !game.hasHint) return;
    const id = window.setTimeout(() => {
      if (!nudgedRef.current) {
        nudgedRef.current = true;
        toast.show(pickNudge(active.startedAt));
      }
    }, HINT_NUDGE_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.state, active?.status, settings.hintNudges, game.hasHint]);

  const parkedKeys = useMemo(() => new Set(game.parked.map((p) => p.key)), [game.parked]);
  const upNext = useMemo(() => {
    if (!active || active.playMode !== 'daily' || active.archive || active.seed.dayNumber !== today) return null;
    const garden = gardenFor(stats, today, allModes().filter((m) => isModeEnabled(m.id)), parkedKeys);
    const mode = nextUnplayed(garden, active.mode.id);
    const remaining = garden.entries.filter((e) => e.plot === 'open' && e.mode.id !== active.mode.id).length;
    return mode ? { mode, remaining } : null;
  }, [active, stats, today, isModeEnabled, parkedKeys]);

  if (!active) {
    return (
      <div className="cv-empty">
        <p>No puzzle in progress.</p>
        <Button variant="primary" onClick={() => navigate('home')}>
          Back home
        </Button>
      </div>
    );
  }

  const Board = active.mode.Board;
  const playing = active.status === 'playing';
  const view = viewFromSession(active);

  const goHome = () => {
    // An unfinished puzzle stays parked; it can be continued from Home.
    game.leave();
    navigate('home');
  };
  const playAgain = () =>
    game.start({
      modeId: active.mode.id,
      playMode: 'endless',
      difficultyId: active.requestedDifficultyId,
      fresh: true,
    });
  const playUpNext = () => {
    if (upNext) game.start({ modeId: upNext.mode.id, playMode: 'daily' });
  };
  const closeHowto = () => {
    setHowtoOpen(false);
    // Reading the rules before the first move shouldn't cost time on the clock.
    if (autoHowto) {
      setAutoHowto(false);
      game.restartClock();
    }
  };

  const label = active.playMode === 'daily' ? `Daily #${active.seed.dayNumber}${active.archive ? ' · archive' : ''}` : 'Endless';

  return (
    <div className="cv-play cv-column" style={accentVars(active.mode.accent)}>
      <div className="cv-play__header">
        <div className="cv-row" style={{ gap: 'var(--space-xs)' }}>
          <IconButton
            icon="back"
            label={playing ? 'Back home — your progress is saved' : 'Back home'}
            onClick={goHome}
          />
          <div className="cv-play__heading">
            <span className="cv-play__title">{active.mode.name}</span>
            <span className="cv-play__sub">
              <span>{label}</span>
              <span aria-hidden="true">·</span>
              <span>{active.difficulty.label}</span>
              {playing && !active.mode.ownTimer ? (
                <>
                  <span aria-hidden="true">·</span>
                  <ElapsedTime startedAt={active.startedAt} running={playing} />
                </>
              ) : null}
            </span>
          </div>
        </div>
        <IconButton icon="bulb" label="How to play" onClick={() => setHowtoOpen(true)} />
      </div>

      {active.challenge?.beat ? (
        <p className="cv-play__challenge">
          <span aria-hidden="true">🏁</span> Beat {active.challenge.by ? `${active.challenge.by}’s` : 'a friend’s'}{' '}
          <strong>{commas(active.challenge.beat)}</strong>
        </p>
      ) : null}

      <div className="cv-play__board">
        <Board
          key={active.seed.seed}
          state={active.state}
          status={active.status}
          dispatch={game.dispatchMove}
          feedback={active.feedback}
          reducedMotion={effectiveReducedMotion}
          startedAt={active.startedAt}
        />
      </div>

      {playing ? (
        <div className="cv-play__footer">
          <Button
            variant="secondary"
            block
            iconLeft="bulb"
            disabled={!game.hasHint}
            onClick={game.requestHint}
          >
            {active.hintsUsed > 0 ? `Hint · ${active.hintsUsed} used` : 'Need a hint?'}
          </Button>
          <button type="button" className="cv-play__giveup" onClick={() => setConfirmGiveUp(true)}>
            Give up
          </button>
        </div>
      ) : view && !resultOpen ? (
        <div className="cv-play__footer">
          <Button variant="primary" block iconLeft="trophy" onClick={() => setResultOpen(true)}>
            View result
          </Button>
        </div>
      ) : null}

      <HowToPlayModal mode={active.mode} open={howtoOpen} onClose={closeHowto} />

      <Modal
        open={confirmGiveUp}
        onClose={() => setConfirmGiveUp(false)}
        title="Give up this puzzle?"
        footer={
          <>
            <Button variant="ghost" block onClick={() => setConfirmGiveUp(false)}>
              Keep playing
            </Button>
            <Button
              variant="danger"
              block
              onClick={() => {
                setConfirmGiveUp(false);
                game.forfeit();
              }}
            >
              Give up
            </Button>
          </>
        }
      >
        <p className="cv-muted">
          {active.playMode === 'daily' && !active.archive
            ? 'It counts as a loss for today’s daily and reveals the answer.'
            : 'It counts as a loss and reveals the answer.'}{' '}
          To take a break instead, just head back — your progress is saved.
        </p>
      </Modal>

      {view && resultOpen ? (
        <ResultModal
          view={view}
          onClose={() => setResultOpen(false)}
          onHome={goHome}
          onPlayAgain={playAgain}
          upNext={upNext}
          onUpNext={playUpNext}
        />
      ) : null}
    </div>
  );
}
